import React from "react";
import ReportTable from "../ReportTable";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import useReportingYearLabels from "./useReportingYearLabels";
import MultiHeaderReportTable from "../MultiHeaderReportTable";

const valueTableRows = (obj, keys, idPrefix) =>
  keys.map(([label, key], idx) => ({
    _id: `${idPrefix}-${idx}`,
    parameter: label,
    current: safeText(
      obj?.[key]?.currentFinancialYear ??
        obj?.[key]?.currentFinacialYear ??
        obj?.energyIntensity?.[key]?.currentFinancialYear,
      "N/A",
    ),
    previous: safeText(
      obj?.[key]?.previousFinancialYear ??
        obj?.[key]?.previousFinacialYear ??
        obj?.energyIntensity?.[key]?.previousFinancialYear,
      "N/A",
    ),
  }));

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const readFYCell = (cell, which) => {
  if (!cell || typeof cell !== "object") return "";
  if (which === "current") {
    return cell.currentFinancialYear ?? cell.currentFinacialYear ?? "";
  }
  return cell.previousFinancialYear ?? cell.previousFinacialYear ?? "";
};

/** Sum FY columns across named keys (waste / water withdrawal style cells). */
const sumFYCells = (obj, keys, which) => {
  let sum = 0;
  let any = false;
  keys.forEach((key) => {
    const raw = readFYCell(obj?.[key], which);
    const n = toNum(String(raw).replace(/,/g, ""));
    if (n !== null) {
      sum += n;
      any = true;
    }
  });
  return any ? String(sum) : "N/A";
};

/** Sum dischargeWithoutTreatment + dischargeAfterTreatment for each source key. */
const sumDischargeSources = (root, keys) => {
  let sum = 0;
  let any = false;
  keys.forEach((k) => {
    const o = root?.[k] || {};
    const t1 = toNum(o.dischargeWithoutTreatment);
    const t2 = toNum(o.dischargeAfterTreatment);
    if (t1 !== null) {
      sum += t1;
      any = true;
    }
    if (t2 !== null) {
      sum += t2;
      any = true;
    }
  });
  return any ? String(sum) : "N/A";
};

const Principle6 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(6);
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const wasteMgmt = essential?.wasteManagementDetails;
  const energyConsumption = essential?.energyConsumptionIntensity || essential;

  const { currentYear, previousYear } = useReportingYearLabels();
  const fyCurrentColLabel = currentYear
    ? `In FY ${currentYear} (Current Financial Year)`
    : "Current Financial Year (N/A)";
  const fyPreviousColLabel = previousYear
    ? `In FY ${previousYear} (Previous Financial Year)`
    : "Previous Financial Year (N/A)";

  const wasteIntensityCurrentHdr = currentYear
    ? `FY ${currentYear} (Current Financial Year)`
    : "Current Financial Year (N/A)";
  const wasteIntensityPreviousHdr = previousYear
    ? `FY ${previousYear} (Previous Financial Year)`
    : "Previous Financial Year (N/A)";

  const energyKeys = [
    ["Total electricity consumption (kWh)", "electricityConsumption"],
    ["Total fuel consumption (litres)", "fuelConsumption"],
    ["Energy consumption through other sources (kWh)", "energyConsumption"],
  ];

  const energyRenewableRowsBase = valueTableRows(
    energyConsumption?.fromRenewableSource,
    energyKeys,
    "p6-e1-ren",
  );
  const energyNonRenewableRowsBase = valueTableRows(
    energyConsumption?.fromNonRenewableSource,
    energyKeys,
    "p6-e1-non",
  );

  const energyRenewableRows = [
    ...energyRenewableRowsBase,
    {
      _id: "p6-e1-ren-total",
      parameter: "Total energy consumption from renewable sources (kWh)",
      current: (() => {
        const vals = energyRenewableRowsBase
          .map((r) => toNum(r.current))
          .filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
      previous: (() => {
        const vals = energyRenewableRowsBase
          .map((r) => toNum(r.previous))
          .filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
    },
  ];

  const energyNonRenewableRows = [
    ...energyNonRenewableRowsBase,
    {
      _id: "p6-e1-non-total",
      parameter: "Total energy consumption from non-renewable sources (kWh)",
      current: (() => {
        const vals = energyNonRenewableRowsBase
          .map((r) => toNum(r.current))
          .filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
      previous: (() => {
        const vals = energyNonRenewableRowsBase
          .map((r) => toNum(r.previous))
          .filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
    },
  ];

  const energyIntensityRows = valueTableRows(
    energyConsumption?.energyIntensity,
    [
      [
        "Total energy consumption from renewable & non-renewable sources (kWh)",
        "energyIntensity",
      ],
      [
        "Energy intensity per rupee of turnover (kWh)",
        "energyIntensityTurnover",
      ],
      [
        "Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (kWh)",
        "energyIntensityPuchasingPower",
      ],
      [
        "Energy intensity in terms of physical output (kWh)",
        "energyPhysicalOutput",
      ],
      ["Energy intensity (optional) (kWh)", "energyIntensityOptional"],
    ],
    "p6-e1-int",
  );

  const waterRowsSpec = [
    ["Surface water", "surfaceWater"],
    ["Ground water", "groundWater"],
    ["Third party water", "thirdPartyWater"],
    ["Sea water / desalinated water", "seaWater"],
    ["Others", "others"],
    ["Total volume of water withdrawal", "totalVolumnWithdrawal"],
    ["Total volume of water consumption", "totalWaterConsumption"],
    ["Water intensity per rupee of turnover", "waterIntensityTurnover"],
    ["Water intensity per rupee of PPP", "waterIntensityPuchasingPower"],
    [
      "Water intensity in terms of physical output",
      "waterIntensityPhyiscalOutput",
    ],
    ["Water intensity (optional)", "waterIntensityOptional"],
  ];
  const wdRelated = essential?.waterDisclosuresRelated ?? {};
  const waterWithdrawalSumKeys = [
    "surfaceWater",
    "groundWater",
    "thirdPartyWater",
    "seaWater",
    "others",
  ];
  const waterRowsBase = valueTableRows(wdRelated, waterRowsSpec, "p6-e3");
  const waterRows = waterRowsBase.map((row) => {
    if (row.parameter === "Total volume of water withdrawal") {
      const sC = sumFYCells(wdRelated, waterWithdrawalSumKeys, "current");
      const sP = sumFYCells(wdRelated, waterWithdrawalSumKeys, "previous");
      return {
        ...row,
        current: sC !== "N/A" ? sC : row.current,
        previous: sP !== "N/A" ? sP : row.previous,
      };
    }
    return row;
  });

  const readTreatmentLevel = (obj) =>
    safeText(obj?.treatmentLevel ?? obj?.treatMentLeval, "-");

  const waterDischargeRows = [
    ["To surface water", "surfaceWater"],
    ["To Ground water", "groundWater"],
    ["To sea water", "producedWater"],
    ["Sent to third parties", "thirdPartyWater"],
    ["Others", "other"],
  ].flatMap(([label, key], idx) => {
    const c = essential?.waterDischargeCurrent?.[key] || {};
    const p = essential?.waterDischargePrevious?.[key] || {};
    return [
      {
        _id: `p6-e4-${idx}-0`,
        parameter: label,
        treatmentType: "Not treated",
        currentDischarge: safeText(c?.dischargeWithoutTreatment, "N/A"),
        currentTreatment: "-",
        previousDischarge: safeText(p?.dischargeWithoutTreatment, "N/A"),
        previousTreatment: "-",
      },
      {
        _id: `p6-e4-${idx}-1`,
        parameter: "",
        treatmentType: "With treatment",
        currentDischarge: safeText(c?.dischargeAfterTreatment, "N/A"),
        currentTreatment: readTreatmentLevel(c),
        previousDischarge: safeText(p?.dischargeAfterTreatment, "N/A"),
        previousTreatment: readTreatmentLevel(p),
      },
    ];
  });

  const essentialDischargeKeysForTotal = [
    "surfaceWater",
    "groundWater",
    "producedWater",
    "thirdPartyWater",
    "other",
  ];
  const waterDischargeTotalComputedCurrent = sumDischargeSources(
    essential?.waterDischargeCurrent,
    essentialDischargeKeysForTotal,
  );
  const waterDischargeTotalComputedPrevious = sumDischargeSources(
    essential?.waterDischargePrevious,
    essentialDischargeKeysForTotal,
  );

  const waterDischargeTotalRow = {
    _id: "p6-e4-total",
    parameter: "Total water discharged (in kilolitres)",
    treatmentType: "",
    currentDischarge:
      waterDischargeTotalComputedCurrent !== "N/A"
        ? waterDischargeTotalComputedCurrent
        : safeText(essential?.waterDischargeCurrent?.total, "N/A"),
    currentTreatment: "",
    previousDischarge:
      waterDischargeTotalComputedPrevious !== "N/A"
        ? waterDischargeTotalComputedPrevious
        : safeText(essential?.waterDischargePrevious?.total, "N/A"),
    previousTreatment: "",
  };

  const ghgRows = valueTableRows(
    essential?.greenHouseGasEmissions,
    [
      [
        "Total Scope 1 emissions Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
        "scope1",
      ],
      [
        "Total Scope 2 emissions Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
        "scope2",
      ],
      [
        "Total Scope 1 and Scope 2 emission intensity per rupee of turnover",
        "totalOfScope1and2Intensity",
      ],
      [
        "Total Scope 1 and Scope 2 emissions per rupee of turnover adjusted for Purchasing Power Parity (PPP)",
        "scope1and2emissionPPP",
      ],
      [
        "Total Scope 1 and Scope 2 emission intensity in terms of physical output",
        "scope1and2emissionIntensityPhysical",
      ],
      [
        "Total Scope 1 and Scope 2 emission intensity (optional)",
        "scope1and2EmissionIntensity",
      ],
    ],
    "p6-e7",
  );

  const airRows = valueTableRows(
    essential?.airEmissons,
    [
      ["NOx", "nox"],
      ["Volatile organic compounds (VOC)", "voc"],
      ["SOx", "sox"],
      ["Particulate Matter (PM)", "pm"],
      ["Persistent organic pollutants (POP)", "pop"],
      ["Hazardous air pollutants (HAP)", "hap"],
    ],
    "p6-e6",
  );

  const otherAirRows = (
    Array.isArray(essential?.airEmissionsOthers)
      ? essential.airEmissionsOthers
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e6-other-${idx}`,
    parameter: safeText(row?.parameter, "N/A"),
    current: safeText(row?.currentFinancialYear, "N/A"),
    previous: safeText(row?.previousFinancialYear, "N/A"),
  }));

  const wasteGenDetailSpec = [
    ["Plastic waste", "plastics"],
    ["E-waste", "eWaste"],
    ["Bio-medical waste", "bioMediacal"],
    ["Construction and demolition waste", "construtionAndDemolition"],
    ["Battery waste", "batteryWaste"],
    ["Radioactive waste", "radioactiveWaste"],
    ["Other hazardous waste", "otherHazardous"],
    ["Other non-hazardous waste", "otherNonHazardous"],
  ];
  const wgObj = wasteMgmt?.wasteGenerated ?? essential?.wasteGenerated ?? {};
  const wasteGeneratedRowsBase = valueTableRows(
    wgObj,
    wasteGenDetailSpec,
    "p6-e9-gen",
  );
  const wasteGenSumKeys = wasteGenDetailSpec.map(([, k]) => k);
  const wasteGeneratedRows = [
    ...wasteGeneratedRowsBase,
    {
      _id: "p6-e9-gen-total",
      parameter: "Total",
      current: sumFYCells(wgObj, wasteGenSumKeys, "current"),
      previous: sumFYCells(wgObj, wasteGenSumKeys, "previous"),
    },
  ];

  const wasteRecDetailSpec = [
    ["Recycled", "recycled"],
    ["Re-Used", "reUsed"],
    ["Other recovery operations", "other"],
  ];
  const wrObj =
    wasteMgmt?.wasteGeneratedCategory ??
    essential?.wasteGeneratedCategory ??
    {};
  const wasteRecoveredRowsBase = valueTableRows(
    wrObj,
    wasteRecDetailSpec,
    "p6-e9-rec",
  );
  const wasteRecSumKeys = wasteRecDetailSpec.map(([, k]) => k);
  const wasteRecoveredRows = [
    ...wasteRecoveredRowsBase,
    {
      _id: "p6-e9-rec-total",
      parameter: "Total",
      current: sumFYCells(wrObj, wasteRecSumKeys, "current"),
      previous: sumFYCells(wrObj, wasteRecSumKeys, "previous"),
    },
  ];

  const wasteDispDetailSpec = [
    ["Incineration", "inceneration"],
    ["Landfilling", "lanfilling"],
    ["Other disposal operations", "other"],
  ];
  const wdObj = wasteMgmt?.wasteDesposed ?? essential?.wasteDesposed ?? {};
  const wasteDisposedRowsBase = valueTableRows(
    wdObj,
    wasteDispDetailSpec,
    "p6-e9-dis",
  );
  const wasteDispSumKeys = wasteDispDetailSpec.map(([, k]) => k);
  const wasteDisposedRows = [
    ...wasteDisposedRowsBase,
    {
      _id: "p6-e9-dis-total",
      parameter: "Total",
      current: sumFYCells(wdObj, wasteDispSumKeys, "current"),
      previous: sumFYCells(wdObj, wasteDispSumKeys, "previous"),
    },
  ];
  const wi = wasteMgmt?.wasteIntensity ?? essential?.wasteIntensity ?? {};
  const wasteIntensityRows = [
    {
      _id: "p6-wi-0",
      parameter:
        "Waste intensity per rupee of turnover (Total waste generated / Revenue from operations)",
      current: safeText(
        wi?.wasteIntensity?.currentFinancialYear ??
          wi?.wasteIntensity?.currentFinacialYear,
        "N/A",
      ),
      previous: safeText(
        wi?.wasteIntensity?.previousFinancialYear ??
          wi?.wasteIntensity?.previousFinacialYear,
        "N/A",
      ),
    },
    {
      _id: "p6-wi-1",
      parameter:
        "Waste intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total waste generated / Revenue from operations adjusted for PPP)",
      current: safeText(
        wi?.wasteIntensityPPP?.currentFinancialYear ??
          wi?.wasteIntensityPPP?.currentFinacialYear,
        "N/A",
      ),
      previous: safeText(
        wi?.wasteIntensityPPP?.previousFinancialYear ??
          wi?.wasteIntensityPPP?.previousFinacialYear,
        "N/A",
      ),
    },
    {
      _id: "p6-wi-2",
      parameter: "Waste intensity in terms of physical output",
      current: safeText(
        wi?.wasteIntensityPhysical?.currentFinancialYear ??
          wi?.wasteIntensityPhysical?.currentFinacialYear,
        "N/A",
      ),
      previous: safeText(
        wi?.wasteIntensityPhysical?.previousFinancialYear ??
          wi?.wasteIntensityPhysical?.previousFinacialYear,
        "N/A",
      ),
    },
    {
      _id: "p6-wi-3",
      parameter:
        "Waste intensity (optional) – the relevant metric may be selected by the entity",
      current: safeText(
        wi?.wasteIntensityOptiomal?.currentFinancialYear ??
          wi?.wasteIntensityOptiomal?.currentFinacialYear,
        "N/A",
      ),
      previous: safeText(
        wi?.wasteIntensityOptiomal?.previousFinancialYear ??
          wi?.wasteIntensityOptiomal?.previousFinacialYear,
        "N/A",
      ),
    },
  ];

  const wasteIndependentAssessment =
    wasteMgmt?.independentAssessment ?? essential?.independentAssessment;

  const projectRows = (
    Array.isArray(essential?.projectUndertakenByentity)
      ? essential.projectUndertakenByentity
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e12-${idx}`,
    project: safeText(row?.nameOfProject, "N/A"),
    brief: safeText(row?.projectBrief, "N/A"),
    notification: safeText(row?.EIANotification, "N/A"),
    date: safeText(row?.dateOfNotification, "N/A"),
    publicDomain: safeText(row?.publicDomain, "N/A"),
    agency: safeText(row?.extenalAgecy, "N/A"),
    link: safeText(row?.link, "N/A"),
  }));

  const environmentalApprovalRows = (
    Array.isArray(essential?.environmentalApproval)
      ? essential.environmentalApproval
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e10-${idx}`,
    location: safeText(row?.location, "N/A"),
    operation: safeText(row?.operation, "N/A"),
    approval: safeText(row?.approval, "N/A"),
    reason: safeText(row?.reason, "N/A"),
  }));

  const nonComplianceRows = (
    Array.isArray(essential?.entityComplainwithEnvironmentLaw)
      ? essential.entityComplainwithEnvironmentLaw
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e12-${idx}`,
    law: safeText(row?.specifyLaw, "N/A"),
    details: safeText(row?.details, "N/A"),
    penalties: safeText(row?.penalties, "N/A"),
    correctiveAction: safeText(row?.correctiveAction, "N/A"),
  }));

  const l1Facilities = Array.isArray(
    leadership?.waterWithDrawalConsumptionAndDischarg,
  )
    ? leadership.waterWithDrawalConsumptionAndDischarg
    : [];

  const l1WithdrawalSumKeys = [
    "surfaceWater",
    "groundWater",
    "thirdPartyWater",
    "seaWater",
    "other",
  ];

  const l1WithdrawalRows = (facility, idx) => {
    const detailSpec = [
      ["(i) Surface water", "surfaceWater"],
      ["(ii) Ground water", "groundWater"],
      ["(iii) Third party water", "thirdPartyWater"],
      ["(iv) Seawater / desalinated water", "seaWater"],
      ["(v) Others", "other"],
    ];
    const w = facility?.waterWithdrawal;
    const detailRows = detailSpec.map(([parameter, key], rowIdx) => ({
      _id: `p6-l1-w-${idx}-${rowIdx}`,
      parameter,
      current: safeText(readFYCell(w?.[key], "current"), "N/A"),
      previous: safeText(readFYCell(w?.[key], "previous"), "N/A"),
    }));

    const totalCurrent = sumFYCells(w, l1WithdrawalSumKeys, "current");
    const totalPrevious = sumFYCells(w, l1WithdrawalSumKeys, "previous");

    const tailSpec = [
      ["Total volume of water withdrawal", "total"],
      [
        "Total volume of water consumption (in kiloliters)",
        "totalWaterConsumption",
      ],
      [
        "Water intensity per rupee of turnover (Water consumed / turnover)",
        "waterIntensityPerRupee",
      ],
      [
        "Water intensity (optional) - the relevant metric may be selected by the entity",
        "waterIntensity",
      ],
    ];
    const tailRows = tailSpec.map(([parameter, key], rowIdx) => {
      const rowIdxActual = detailSpec.length + rowIdx;
      if (key === "total") {
        return {
          _id: `p6-l1-w-${idx}-${rowIdxActual}`,
          parameter,
          current:
            totalCurrent !== "N/A"
              ? totalCurrent
              : safeText(readFYCell(w?.total, "current"), "N/A"),
          previous:
            totalPrevious !== "N/A"
              ? totalPrevious
              : safeText(readFYCell(w?.total, "previous"), "N/A"),
        };
      }
      return {
        _id: `p6-l1-w-${idx}-${rowIdxActual}`,
        parameter,
        current: safeText(readFYCell(w?.[key], "current"), "N/A"),
        previous: safeText(readFYCell(w?.[key], "previous"), "N/A"),
      };
    });

    return [...detailRows, ...tailRows];
  };

  const l1DischargeRows = (facility, idx) => {
    const mapKeys = [
      ["To surface water", "toSurfaceWater"],
      ["To Ground water", "toGroundwater"],
      ["To sea water", "toProducedWater"],
      ["Sent to third parties", "toThirdParties"],
      ["Others", "others"],
    ];
    const rows = mapKeys.flatMap(([label, key], rowIdx) => {
      const c = facility?.waterDischargeCurrent?.[key] || {};
      const p = facility?.waterDischargePrevious?.[key] || {};
      return [
        {
          _id: `p6-l1-d-${idx}-${rowIdx}-0`,
          parameter: label,
          treatmentType: "Not treated",
          currentDischarge: safeText(c?.dischargeWithoutTreatment, "N/A"),
          currentTreatment: "-",
          previousDischarge: safeText(p?.dischargeWithoutTreatment, "N/A"),
          previousTreatment: "-",
        },
        {
          _id: `p6-l1-d-${idx}-${rowIdx}-1`,
          parameter: "",
          treatmentType: "With treatment",
          currentDischarge: safeText(c?.dischargeAfterTreatment, "N/A"),
          currentTreatment: safeText(c?.treatmentLevel, "-"),
          previousDischarge: safeText(p?.dischargeAfterTreatment, "N/A"),
          previousTreatment: safeText(p?.treatmentLevel, "-"),
        },
      ];
    });

    const l1DischargeSumKeys = [
      "toSurfaceWater",
      "toGroundwater",
      "toProducedWater",
      "toThirdParties",
      "others",
    ];
    const l1DischargeCurComp = sumDischargeSources(
      facility?.waterDischargeCurrent,
      l1DischargeSumKeys,
    );
    const l1DischargePrevComp = sumDischargeSources(
      facility?.waterDischargePrevious,
      l1DischargeSumKeys,
    );

    rows.push({
      _id: `p6-l1-d-${idx}-total`,
      parameter: "Total water discharged",
      treatmentType: "",
      currentDischarge:
        l1DischargeCurComp !== "N/A"
          ? l1DischargeCurComp
          : safeText(facility?.waterDischargeCurrent?.total, "N/A"),
      currentTreatment: "",
      previousDischarge:
        l1DischargePrevComp !== "N/A"
          ? l1DischargePrevComp
          : safeText(facility?.waterDischargePrevious?.total, "N/A"),
      previousTreatment: "",
    });
    return rows;
  };

  const l2Rows = valueTableRows(
    leadership?.scope3EmissionIntensity,
    [
      [
        "Total Scope 3 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
        "totalScope3Emission",
      ],
      [
        "Total Scope 3 emissions per rupee turnover",
        "totalScope3EmissionPerRupeeturnOver",
      ],
      [
        "Total Scope 3 emission intensity (optional) - the relevant metric may be selected by the entity",
        "emissionIntensity",
      ],
    ],
    "p6-l2",
  );

  const l4Rows = (
    Array.isArray(leadership?.spcificIntiativesorUsedInnovative)
      ? leadership.spcificIntiativesorUsedInnovative
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-l4-${idx}`,
    initiative: safeText(row?.intiativeundertaken, "N/A"),
    detail: safeText(row?.detail, "N/A"),
    webLink: safeText(row?.webLink, "N/A"),
    outcome: safeText(row?.outCome, "N/A"),
  }));

  return (
    <div className={styles.principleBlock}>
      <div
        className={styles.principleHeading}
      >{`PRINCIPLE 6: ${principleTitles[5]}`}</div>
      {loading ? (
        <div className={styles.meta}>Loading principle 6...</div>
      ) : null}

      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>

      <div className={styles.question}>
        1. Details of total energy consumption (in Joules or multiples) and
        energy intensity.
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        a. From Renewable Sources
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>a. From Renewable Sources</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={energyRenewableRows}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>b. From Non-Renewable Sources</div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        b. From Non-Renewable Sources
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={energyNonRenewableRows}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        c. Energy Intensity
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={energyIntensityRows}
      />

      <div className={styles.question}>
        2. Does the entity have any sites / facilities identified as designated
        consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of
        the Government of India? If yes, disclose whether targets set under the
        PAT scheme have been achieved. In case targets have not been achieved,
        provide the remedial action taken, if any.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.patSchemeDetails?.action)}
        {essential?.patSchemeDetails?.action
          ? `, ${safeText(essential?.patSchemeDetails?.remark, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>
        3. Provide details of the following disclosures related to water, in the
        following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={waterRows}
      />
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out
        by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(
          essential?.waterDisclosuresRelated?.externalAgency?.action,
        )}
        {String(essential?.waterDisclosuresRelated?.externalAgency?.action) ===
        "true"
          ? `, ${safeText(
              Array.isArray(
                essential?.waterDisclosuresRelated?.externalAgency?.name,
              )
                ? essential.waterDisclosuresRelated.externalAgency.name
                    .filter(Boolean)
                    .join(", ")
                : essential?.waterDisclosuresRelated?.externalAgency?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        4. Provide the following details related to water discharged: Water
        discharge by destination and level of treatment (in kilolitres)
      </div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "treatmentType", label: "" },
          {
            label: fyCurrentColLabel,
            children: [
              { field: "currentDischarge", label: "Water Discharge (in KL)" },
              {
                field: "currentTreatment",
                label: "Specify level of treatment",
              },
            ],
          },
          {
            label: fyPreviousColLabel,
            children: [
              { field: "previousDischarge", label: "Water Discharged (in KL)" },
              {
                field: "previousTreatment",
                label: "Specify level of treatment",
              },
            ],
          },
        ]}
        rows={[...waterDischargeRows, waterDischargeTotalRow]}
      />
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out
        by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.waterDischargeExternalAgency?.action)}
        {String(essential?.waterDischargeExternalAgency?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.waterDischargeExternalAgency?.name)
                ? essential.waterDischargeExternalAgency.name
                    .filter(Boolean)
                    .join(", ")
                : essential?.waterDischargeExternalAgency?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        5. Has the entity implemented a mechanism for Zero Liquid Discharge? If
        yes, provide details of its coverage and implementation.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.zeroLiquidDischarge?.action)}
        {essential?.zeroLiquidDischarge?.action
          ? `, ${safeText(essential?.zeroLiquidDischarge?.remark, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>
        6. Please provide details of air emissions (other than GHG emissions) by
        the entity, in the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={airRows}
      />
      {otherAirRows.length ? (
        <>
          <div className={`${styles.answer} ${styles.boldAnswer}`}>
            Other air emission parameters (if any)
          </div>
          <ReportTable
            showSerialNumber={false}
            columns={[
              { field: "parameter", label: "Category" },
              { field: "current", label: fyCurrentColLabel },
              { field: "previous", label: fyPreviousColLabel },
            ]}
            rows={otherAirRows}
          />
        </>
      ) : null}
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out
        by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.airEmissionAssessment?.action)}
        {String(essential?.airEmissionAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.airEmissionAssessment?.name)
                ? essential.airEmissionAssessment.name
                    .filter(Boolean)
                    .join(", ")
                : essential?.airEmissionAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        7. Provide details of greenhouse gas emissions (Scope 1 and Scope 2
        emissions) & its intensity, in the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={ghgRows}
      />

      <div className={styles.question}>
        8. Does the entity have any project related to reducing Green House Gas
        emission? If Yes, then provide details.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.greenHouseGasBooleanEmissions?.action)}
        {essential?.greenHouseGasBooleanEmissions?.action
          ? `, ${safeText(essential?.greenHouseGasBooleanEmissions?.remark, "N/A")}`
          : ""}
      </div>
      {/* <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.independentAssessment?.action)}
        {String(essential?.independentAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.independentAssessment?.name)
                ? essential.independentAssessment.name.filter(Boolean).join(", ")
                : essential?.independentAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div> */}

      <div className={styles.question}>
        9. Provide details related to waste management by the entity
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        a. Waste generated (in metric tonnes)
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={wasteGeneratedRows}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        b. Waste intensity
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameters", width: "46%" },
          { field: "current", label: wasteIntensityCurrentHdr },
          { field: "previous", label: wasteIntensityPreviousHdr },
        ]}
        rows={wasteIntensityRows}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        c. For each category of waste generated, total waste recovered through
        recycling, re-using or other recovery operations (in metric tonnes)
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={wasteRecoveredRows}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        d. For each category of waste generated, total waste disposed by nature
        of disposal method (in metric tonnes)
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={wasteDisposedRows}
      />
      <div className={styles.question}>
        Indicate if any independent assessment/ evaluation/assurance has been
        carried out by an external agency? If yes, name of the external agency.
      </div>
      <div className={styles.answer}>
        {renderYesNo(wasteIndependentAssessment?.action)}
        {String(wasteIndependentAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(wasteIndependentAssessment?.name)
                ? wasteIndependentAssessment.name.filter(Boolean).join(", ")
                : wasteIndependentAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        10. Briefly describe the waste management practices adopted in your
        establishments. Describe the strategy adopted by your company to reduce
        usage of hazardous and toxic chemicals in your products and processes
        and the practices adopted to manage such wastes.
      </div>
      <div className={styles.answer}>
        Provide steps taken to address the same.
        <br />
        {safeText(essential?.wasterManagmentPractices, "N/A")}
      </div>

      <div className={styles.question}>
        11. If the entity has operations/offices in/around ecologically
        sensitive sensitive areas (such as national parks, wildlife sanctuaries,
        biosphere reserves, wetlands, biodiversity hotspots, forests, coastal
        regulation zones etc.) where environmental approvals / clearances are
        required, please specify details in the following format:
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          { field: "location", label: "Location of operations/offices" },
          { field: "operation", label: "Type of operations" },
          {
            field: "approval",
            label:
              "Whether the conditions of environmental approval / clearance are being complied with? (Y/N)",
          },
          {
            field: "reason",
            label:
              "If no, the reasons thereof and corrective action taken, if any",
          },
        ]}
        rows={environmentalApprovalRows}
      />

      <div className={styles.question}>
        12. Details of environmental impact assessments of projects undertaken
        by the entity based on applicable laws, in the current financial year:
      </div>
      {essential?.eiaEnabled ? (
        <ReportTable
          showSerialNumber={false}
          columns={[
            { field: "project", label: "Name of Project" },
            { field: "brief", label: "Brief Details of Project" },
            { field: "notification", label: "EIA Notification No." },
            { field: "date", label: "Date of Notification" },
            {
              field: "agency",
              label: "Whether conducted by independent external agency",
            },
            {
              field: "publicDomain",
              label: "Results communicated in public domain",
            },
            { field: "link", label: "Link" },
          ]}
          rows={projectRows}
        />
      ) : (
        safeText("N/A", "N/A")
      )}

      <div className={styles.question}>
        13. Is the entity compliant with the applicable environmental law/
        regulations/ guidelines in India; such as the Water (Prevention and
        Control of Pollution) Act, Air (Prevention and Control of Pollution)
        Act, Environment protection act and rules thereunder. If not, provide
        details of all such non-compliances, in the following format:
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.entityComplain)}
      </div>
      {String(essential?.entityComplain) === "true" ? (
        <ReportTable
          showSerialNumber={true}
          columns={[
            {
              field: "law",
              label: "Specify the law / regulation / guidelines (not complied)",
            },
            {
              field: "details",
              label: "Provide details of the non- compliance",
            },
            {
              field: "penalties",
              label:
                "Any fines / penalties / action taken by regulatory agencies such as pollution control boards or by courts",
            },
            {
              field: "correctiveAction",
              label: "Corrective action taken, if any",
            },
          ]}
          rows={nonComplianceRows}
        />
      ) : null}

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>

      <div className={styles.question}>
        1. Water withdrawal, consumption and discharge in areas of water stress
        (in kilolitres): For each facility / plant located in areas of water
        stress, provide the following information:
      </div>
      {l1Facilities.map((facility, idx) => (
        <div key={facility?._id || `p6-l1-f-${idx}`}>
          <div className={`${styles.answer} ${styles.boldAnswer}`}>
            {`Facility: ${safeText(facility?.facilityName, "N/A")}`}
          </div>
          <div className={`${styles.answer} ${styles.boldAnswer}`}>
            {`(i) Name of area: ${safeText(facility?.nameOfArea, "N/A")}`}
          </div>
          <div className={`${styles.answer} ${styles.boldAnswer}`}>
            {`(ii) Nature of Operation: ${safeText(facility?.operation, "N/A")}`}
          </div>
          <div className={`${styles.answer} ${styles.boldAnswer}`}>
            (iii) Water withdrawal, consumption and discharge by Source
          </div>
          <div className={`${styles.answer} ${styles.boldAnswer}`}>
            Water withdrawal by source (in kiloliters)
          </div>
          <ReportTable
            showSerialNumber={false}
            columns={[
              { field: "parameter", label: "Parameter" },
              { field: "current", label: fyCurrentColLabel },
              { field: "previous", label: fyPreviousColLabel },
            ]}
            rows={l1WithdrawalRows(facility, idx)}
          />
          <MultiHeaderReportTable
            showSerialNumber={false}
            columns={[
              { field: "parameter", label: "Parameter" },
              { field: "treatmentType", label: "" },
              {
                label: fyCurrentColLabel,
                children: [
                  {
                    field: "currentDischarge",
                    label: "Water Discharge (in KL)",
                  },
                  {
                    field: "currentTreatment",
                    label: "Specify level of treatment",
                  },
                ],
              },
              {
                label: fyPreviousColLabel,
                children: [
                  {
                    field: "previousDischarge",
                    label: "Water Discharged (in KL)",
                  },
                  {
                    field: "previousTreatment",
                    label: "Specify level of treatment",
                  },
                ],
              },
            ]}
            rows={l1DischargeRows(facility, idx)}
          />
        </div>
      ))}
      <div className={styles.question}>
        2. Please provide details of total Scope 3 emissions & its intensity, in
        the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={l2Rows}
      />
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out
        by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.waterIndependantAssessment?.action)}
        {String(leadership?.waterIndependantAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(leadership?.waterIndependantAssessment?.name)
                ? leadership.waterIndependantAssessment.name
                    .filter(Boolean)
                    .join(", ")
                : leadership?.waterIndependantAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        3. With respect to the ecologically sensitive areas reported at Question
        11 of Essential Indicators above, provide details of significant direct
        & indirect impact of the entity on biodiversity in such areas along-with
        prevention and remediation activities.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.bioDiversityPrevention, "N/A")}
      </div>

      <div className={styles.question}>
        4. If the entity has undertaken any specific initiatives or used
        innovative technology or solutions to improve resource efficiency, or
        reduce impact due to emissions / effluent discharge / waste generated,
        please provide details of the same as well as outcome of such
        initiatives, as per the following format:
      </div>
      <MultiHeaderReportTable
        showSerialNumber={true}
        columns={[
          { field: "initiative", label: "Initiative undertaken" },
          {
            label: "Details of initiative",
            children: [
              { field: "detail", label: "Summary" },
              { field: "webLink", label: "Weblink" },
            ],
          },
          { field: "outcome", label: "Outcome of the initiative" },
        ]}
        rows={l4Rows}
      />

      <div className={styles.question}>
        5. Does the entity have a business continuity and disaster management
        plan? Give details in 100 words / web link.
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.busineessDisasterPlan?.action)}
      </div>
      {String(leadership?.busineessDisasterPlan?.action) === "true" ? (
        <div className={styles.answer}>
          {safeText(leadership?.busineessDisasterPlan?.remark, "N/A")}
          <br />
          {`Web Link (if any): ${safeText(leadership?.busineessDisasterPlan?.webLink, "N/A")}`}
        </div>
      ) : null}

      <div className={styles.question}>
        6. Disclose any significant adverse impact to the environment, arising
        from the value chain of the entity. What mitigation or adaptation
        measures have been taken by the entity in this regard.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.disCloseAdverseImpactEnvironment, "N/A")}
      </div>

      <div className={styles.question}>
        7. Percentage of value chain partners (by value of business done with
        such partners) that were assessed for environmental impacts.
      </div>
      <div className={styles.answer}>
        <div className={styles.boldAnswer}>a. By the listed entity</div>
        <div>
          {`${safeText(leadership?.greenCreditsByEntityCount, "N/A")} (${safeText(
            leadership?.greenCreditsByEntityRemark,
            "N/A",
          )})`}
        </div>
        <div className={styles.boldAnswer} style={{ marginTop: "10px" }}>
          b. By the top ten (in terms of value of purchases and sales, respectively) value chain partners
        </div>
        <div>
          {`${safeText(leadership?.greenCreditsByValueChainPartnerCount, "N/A")} (${safeText(
            leadership?.greenCreditsByValueChainPartnerRemark,
            "N/A",
          )})`}
        </div>
        {safeText(leadership?.valueChainPartnerAssessment, "N/A")}
      </div>

      <div className={styles.question}>
        8. How many Green Credits have been generated or procured.
      </div>
      <div className={styles.answer}>
        <div className={styles.boldAnswer}>a. By the listed entity</div>
        <div>
          {`${safeText(leadership?.greenCreditsByEntityCount, "N/A")} (${safeText(
            leadership?.greenCreditsByEntityRemark,
            "N/A",
          )})`}
        </div>
        <div className={styles.boldAnswer} style={{ marginTop: "10px" }}>
          b. By the top ten (in terms of value of purchases and sales,
          respectively) value chain partners
        </div>
        <div>
          {`${safeText(leadership?.greenCreditsByValueChainPartnerCount, "N/A")} (${safeText(
            leadership?.greenCreditsByValueChainPartnerRemark,
            "N/A",
          )})`}
        </div>
      </div>

      <div className={styles.question}>
        9. Provide the following details related to water discharged: Water
        discharge by destination and level of treatment (in kilolitres)
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.zeroLiquidDischarge?.action)}
        {leadership?.zeroLiquidDischarge?.action
          ? `, ${safeText(leadership?.zeroLiquidDischarge?.remark, "N/A")}`
          : ""}
      </div>
      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle6;
